import React, { useState, useEffect } from 'react';

/**
 * Muscle Suggestions for quick tag selection (PL & EN)
 */
const PRIMARY_MUSCLE_SUGGESTIONS_PL = [
  'piersiowy większy',
  'najszerszy grzbietu',
  'naramienne',
  'biceps (dwugłowy ramienia)',
  'triceps (trójgłowy ramienia)',
  'czworogłowy uda',
  'kulszowo-goleniowe',
  'pośladkowy większy',
  'prosty brzucha',
  'brzuchaty łydki',
  'czworoboczny (kaptury)',
  'równoległoboczne',
  'prostownik grzbietu',
  'skośne brzucha',
  'przedramiona'
];

const PRIMARY_MUSCLE_SUGGESTIONS_EN = [
  'pectoralis major',
  'latissimus dorsi',
  'deltoids',
  'biceps brachii',
  'triceps brachii',
  'quadriceps',
  'hamstrings',
  'gluteus maximus',
  'rectus abdominis',
  'calves',
  'trapezius',
  'rhomboids',
  'erector spinae',
  'obliques',
  'forearms'
];

const SECONDARY_MUSCLE_SUGGESTIONS_PL = [
  'triceps',
  'przednia część naramiennego',
  'ramienny',
  'mięśnie głębokie (core)',
  'pośladki',
  'dolny grzbiet',
  'zginacze bioder',
  'górny grzbiet',
  'tył barku'
];

const SECONDARY_MUSCLE_SUGGESTIONS_EN = [
  'triceps',
  'anterior deltoid',
  'brachialis',
  'core',
  'glutes',
  'lower back',
  'hip flexors',
  'upper back',
  'rear deltoid'
];

const BODY_PART_OPTIONS = [
  { id: 'chest', labelPL: 'Klatka piersiowa', labelEN: 'Chest' },
  { id: 'back', labelPL: 'Plecy', labelEN: 'Back' },
  { id: 'shoulders', labelPL: 'Barki', labelEN: 'Shoulders' },
  { id: 'upper arms', labelPL: 'Ramiona', labelEN: 'Upper Arms' },
  { id: 'lower arms', labelPL: 'Przedramiona', labelEN: 'Lower Arms' },
  { id: 'upper legs', labelPL: 'Uda i Pośladki', labelEN: 'Upper Legs / Glutes' },
  { id: 'lower legs', labelPL: 'Łydki', labelEN: 'Lower Legs' },
  { id: 'waist', labelPL: 'Brzuch / Pas', labelEN: 'Waist / Core' },
  { id: 'cardio', labelPL: 'Kardio', labelEN: 'Cardio' },
  { id: 'neck', labelPL: 'Szyja', labelEN: 'Neck' }
];

const EQUIPMENT_OPTIONS = [
  { id: 'body weight', labelPL: 'Ciężar ciała', labelEN: 'Body Weight' },
  { id: 'dumbbell', labelPL: 'Hantle', labelEN: 'Dumbbell' },
  { id: 'barbell', labelPL: 'Sztanga', labelEN: 'Barbell' },
  { id: 'cable', labelPL: 'Wyciąg', labelEN: 'Cable' },
  { id: 'leverage machine', labelPL: 'Maszyna', labelEN: 'Leverage Machine' },
  { id: 'band', labelPL: 'Guma oporowa', labelEN: 'Band' },
  { id: 'smith machine', labelPL: 'Suwnica Smitha', labelEN: 'Smith Machine' },
  { id: 'kettlebell', labelPL: 'Kettlebell', labelEN: 'Kettlebell' },
  { id: 'weighted', labelPL: 'Z dociążeniem', labelEN: 'Weighted' },
  { id: 'stability ball', labelPL: 'Piłka gimnastyczna', labelEN: 'Stability Ball' },
  { id: 'ez barbell', labelPL: 'Sztanga łamana', labelEN: 'EZ Barbell' },
  { id: 'other', labelPL: 'Inny', labelEN: 'Other' }
];

/**
 * Modern Clean Two-Column Exercise Form Builder Component for GymGhost
 */
export const ExerciseFormBuilder = ({
  initialData = null,
  isGlobal = false,
  isEdit = false,
  onSubmit,
  onCancel,
  isUploading = false,
  lang = 'pl'
}) => {
  // Active Form Language Tab for editing localization
  const [formLangTab, setFormLangTab] = useState(lang === 'pl' ? 'pl' : 'en');

  // Basic Info State
  const [namePL, setNamePL] = useState('');
  const [nameEN, setNameEN] = useState('');
  const [bodyPart, setBodyPart] = useState('chest');
  const [equipment, setEquipment] = useState('body weight');
  const [subPart, setSubPart] = useState('');

  // Anatomy & Muscle Taxonomy State
  const [targetMuscle, setTargetMuscle] = useState('');
  const [muscleGroup, setMuscleGroup] = useState('');
  const [secondaryMuscles, setSecondaryMuscles] = useState([]);
  const [customSecondaryInput, setCustomSecondaryInput] = useState('');

  // Standard Text Instructions State
  const [instructionsPL, setInstructionsPL] = useState('');
  const [instructionsEN, setInstructionsEN] = useState('');

  // Media State
  const [imageFile, setImageFile] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Populate initial data if editing
  useEffect(() => {
    if (initialData) {
      setNamePL(initialData.namePL || initialData.name || '');
      setNameEN(initialData.nameEN || initialData.name || '');
      setBodyPart(initialData.bodyPart || initialData.category || initialData.body_part || 'chest');
      setEquipment(initialData.equipment || 'body weight');
      setSubPart(initialData.subPart || initialData.sub_part || '');
      setTargetMuscle(initialData.target || initialData.targetMuscle || '');
      setMuscleGroup(initialData.muscle_group || initialData.muscleGroup || '');

      const sec = initialData.secondary_muscles || initialData.secondaryMuscles || [];
      setSecondaryMuscles(Array.isArray(sec) ? sec : (sec ? sec.split(',').map(s => s.trim()).filter(Boolean) : []));

      // Instructions standard text
      const instPL = typeof initialData.instructions === 'object' ? initialData.instructions?.pl : (initialData.hintsPL || initialData.hints || '');
      const instEN = typeof initialData.instructions === 'object' ? initialData.instructions?.en : (initialData.hintsEN || initialData.hints || '');

      setInstructionsPL(instPL || '');
      setInstructionsEN(instEN || '');

      setImageUrlInput(initialData.imageUrl || initialData.gif_url || initialData.image || '');
      setYoutubeUrl(initialData.youtubeUrl || initialData.videoUrl || '');
    }
  }, [initialData]);

  // Handle Tag Management for Secondary Muscles
  const handleAddSecondaryMuscle = (muscleName) => {
    const clean = muscleName.trim();
    if (clean && !secondaryMuscles.includes(clean)) {
      setSecondaryMuscles(prev => [...prev, clean]);
    }
    setCustomSecondaryInput('');
  };

  const handleRemoveSecondaryMuscle = (muscleName) => {
    setSecondaryMuscles(prev => prev.filter(m => m !== muscleName));
  };

  // Drag and Drop File Handlers
  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size > 1 * 1024 * 1024) {
        alert(formLangTab === 'pl'
          ? `⚠️ Plik jest za duży (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maksymalny rozmiar to 1 MB.`
          : `⚠️ Selected file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Max limit is 1 MB.`);
        return;
      }
      setImageFile(file);
    }
  };

  // Submit Handler
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!namePL.trim() && !nameEN.trim()) {
      alert(formLangTab === 'pl' ? 'Wpisz nazwę ćwiczenia.' : 'Please enter exercise name.');
      return;
    }

    const payload = {
      namePL: namePL.trim() || nameEN.trim(),
      nameEN: nameEN.trim() || namePL.trim(),
      name: (formLangTab === 'pl' ? namePL.trim() : nameEN.trim()) || namePL.trim() || nameEN.trim(),
      bodyPart: bodyPart,
      category: bodyPart,
      equipment: equipment,
      subPart: subPart.trim(),
      target: targetMuscle.trim(),
      targetMuscle: targetMuscle.trim(),
      muscle_group: muscleGroup.trim() || bodyPart,
      secondary_muscles: secondaryMuscles,
      instructionsPL: instructionsPL.trim(),
      instructionsEN: instructionsEN.trim(),
      hintsPL: instructionsPL.trim(),
      hintsEN: instructionsEN.trim(),
      hints: formLangTab === 'pl' ? instructionsPL.trim() : instructionsEN.trim(),
      imageFile: imageFile,
      imageUrlInput: imageUrlInput.trim(),
      youtubeUrl: youtubeUrl.trim(),
      isGlobal: isGlobal
    };

    if (onSubmit) {
      await onSubmit(payload);
    }
  };

  // Preview Media URL calculation
  const liveMediaPreviewUrl = imageFile ? URL.createObjectURL(imageFile) : imageUrlInput;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 md:p-8 shadow-2xl space-y-6 max-w-7xl mx-auto">
      {/* Top Header & Language Switcher Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
            <span className="material-symbols-outlined text-xl">fitness_center</span>
          </div>
          <div>
            <h3 className="text-base md:text-lg font-black text-white uppercase tracking-wider m-0">
              {isEdit
                ? (formLangTab === 'pl' ? 'Edycja Ćwiczenia' : 'Edit Movement')
                : (isGlobal
                    ? (formLangTab === 'pl' ? 'Dodaj Nowe Ćwiczenie Globalne' : 'Add New Global Movement')
                    : (formLangTab === 'pl' ? 'Dodaj Nowe Ćwiczenie Autorskie' : 'Add New Custom Movement'))}
            </h3>
            <p className="text-xs text-slate-400 font-medium m-0 mt-0.5">
              {formLangTab === 'pl' ? 'Kreator ćwiczeń z pełną klasyfikacją anatomiczną' : 'Movement builder with anatomical classification'}
            </p>
          </div>
        </div>

        {/* Localized Form View Switcher */}
        <div className="flex items-center gap-1.5 bg-slate-950 p-1.5 rounded-2xl border border-slate-800 shadow-inner">
          <button
            type="button"
            onClick={() => setFormLangTab('pl')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
              formLangTab === 'pl' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span>Polski (PL)</span>
          </button>
          <button
            type="button"
            onClick={() => setFormLangTab('en')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
              formLangTab === 'en' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span>English (EN)</span>
          </button>
        </div>
      </div>

      {/* Main Two-Column Layout Grid */}
      <form onSubmit={handleSubmitForm} className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* LEFT COLUMN: Input Controls & Metadata (~65%) */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-6">
          
          {/* CARD 1: Basic Information & Sub Part */}
          <div className="bg-slate-950/70 p-5 rounded-2xl border border-slate-800 space-y-5 shadow-inner">
            <div className="flex items-center gap-2 text-xs font-black text-indigo-400 uppercase tracking-wider pb-2 border-b border-slate-800/80">
              <span className="material-symbols-outlined text-sm">badge</span>
              <span>1. {formLangTab === 'pl' ? 'Podstawowe Informacje' : 'Basic Information'}</span>
            </div>

            {/* Exercise Names Input */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  {formLangTab === 'pl' ? 'Nazwa Ćwiczenia (PL) *' : 'Exercise Name (PL) *'}
                </label>
                <input
                  type="text"
                  placeholder="np. Wyciskanie Sztangi na Ławce..."
                  value={namePL}
                  onChange={e => setNamePL(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 text-white rounded-xl px-4 py-3 text-xs focus:outline-none focus:border-indigo-500 font-bold shadow-inner"
                  required={!nameEN}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  {formLangTab === 'pl' ? 'Nazwa Ćwiczenia (EN) *' : 'Exercise Name (EN) *'}
                </label>
                <input
                  type="text"
                  placeholder="e.g. Barbell Bench Press..."
                  value={nameEN}
                  onChange={e => setNameEN(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 text-white rounded-xl px-4 py-3 text-xs focus:outline-none focus:border-indigo-500 font-bold shadow-inner"
                  required={!namePL}
                />
              </div>
            </div>

            {/* Body Part Selector Chips */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                {formLangTab === 'pl' ? 'Partia Ciała' : 'Body Part'}
              </label>
              <div className="flex flex-wrap gap-2">
                {BODY_PART_OPTIONS.map((bp) => (
                  <button
                    key={bp.id}
                    type="button"
                    onClick={() => setBodyPart(bp.id)}
                    className={`px-3 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 border ${
                      bodyPart === bp.id
                        ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg shadow-indigo-500/20 scale-[1.02]'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                    }`}
                  >
                    <span>{formLangTab === 'pl' ? bp.labelPL : bp.labelEN}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Equipment Selector Chips */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                {formLangTab === 'pl' ? 'Wymagany Sprzęt' : 'Equipment Required'}
              </label>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT_OPTIONS.map((eq) => (
                  <button
                    key={eq.id}
                    type="button"
                    onClick={() => setEquipment(eq.id)}
                    className={`px-3 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer border ${
                      equipment === eq.id
                        ? 'bg-emerald-600 border-emerald-400 text-white shadow-lg shadow-emerald-500/20 scale-[1.02]'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                    }`}
                  >
                    <span>{formLangTab === 'pl' ? eq.labelPL : eq.labelEN}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sub Part (Detal / Podpartia Ciała) */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>{formLangTab === 'pl' ? 'Podpartia Ciała / Detal' : 'Sub Part (Detail)'}</span>
                <span className="text-[9px] text-slate-500 font-bold lowercase">{formLangTab === 'pl' ? 'opcjonalnie' : 'optional'}</span>
              </label>
              <input
                type="text"
                placeholder={formLangTab === 'pl' ? 'np. Góra klatki, Głowa długa, Czworogłowy...' : 'e.g. Upper chest, Long head, Quads...'}
                value={subPart}
                onChange={e => setSubPart(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 text-white rounded-xl px-4 py-3 text-xs outline-none focus:border-indigo-500 font-semibold shadow-inner"
              />
            </div>
          </div>

          {/* CARD 2: Anatomy & Muscle Engagement (Fully Translated to PL) */}
          <div className="bg-slate-950/70 p-5 rounded-2xl border border-slate-800 space-y-5 shadow-inner">
            <div className="flex items-center gap-2 text-xs font-black text-emerald-400 uppercase tracking-wider pb-2 border-b border-slate-800/80">
              <span className="material-symbols-outlined text-sm">fitness_center</span>
              <span>2. {formLangTab === 'pl' ? 'Anatomia i Zaangażowanie Mięśni' : 'Anatomy & Muscle Engagement'}</span>
            </div>

            {/* Primary Target Muscle Input & Quick Suggestions */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {formLangTab === 'pl' ? 'Główny Mięsień Docelowy' : 'Primary Target Muscle'}
              </label>
              <input
                type="text"
                placeholder={formLangTab === 'pl' ? 'np. piersiowy większy, czworogłowy...' : 'e.g. pectoralis major, biceps...'}
                value={targetMuscle}
                onChange={e => setTargetMuscle(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 text-emerald-400 rounded-xl px-4 py-3 text-xs font-black outline-none focus:border-emerald-500 shadow-inner"
              />
              {/* Quick Suggestion Tags for Target Muscle */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-slate-500 font-bold self-center mr-1">
                  {formLangTab === 'pl' ? 'Szybki wybór:' : 'Quick tags:'}
                </span>
                {(formLangTab === 'pl' ? PRIMARY_MUSCLE_SUGGESTIONS_PL : PRIMARY_MUSCLE_SUGGESTIONS_EN).map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTargetMuscle(tag)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold transition cursor-pointer border ${
                      targetMuscle === tag
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Synergist Muscle Group */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {formLangTab === 'pl' ? 'Grupa Synergistyczna' : 'Synergist Muscle Group'}
              </label>
              <input
                type="text"
                placeholder={formLangTab === 'pl' ? 'np. klatka piersiowa, ramiona...' : 'e.g. chest, upper arms...'}
                value={muscleGroup}
                onChange={e => setMuscleGroup(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 text-white rounded-xl px-4 py-3 text-xs font-semibold outline-none focus:border-indigo-500 shadow-inner"
              />
            </div>

            {/* Secondary / Synergist Muscles Multi-Tag Builder */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {formLangTab === 'pl' ? 'Mięśnie Wspomagające (Synergisty)' : 'Secondary / Synergist Muscles'}
              </label>

              {/* Active Selected Tags */}
              <div className="flex flex-wrap gap-2 p-3 bg-slate-900 border border-slate-800 rounded-xl min-h-[46px] items-center">
                {secondaryMuscles.length === 0 && (
                  <span className="text-xs text-slate-500 italic">
                    {formLangTab === 'pl' ? 'Kliknij tag poniżej lub wpisz własny mięsień...' : 'Click suggestions below or type a muscle...'}
                  </span>
                )}
                {secondaryMuscles.map((muscle) => (
                  <span
                    key={muscle}
                    className="inline-flex items-center gap-1.5 bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 px-3 py-1 rounded-lg text-xs font-extrabold shadow-sm"
                  >
                    <span>{muscle}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSecondaryMuscle(muscle)}
                      className="hover:text-rose-400 text-indigo-400 transition cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-xs">close</span>
                    </button>
                  </span>
                ))}
              </div>

              {/* Tag Input Field */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={formLangTab === 'pl' ? 'Wpisz nazwę mięśnia i naciśnij Enter...' : 'Type muscle and press Enter...'}
                  value={customSecondaryInput}
                  onChange={e => setCustomSecondaryInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSecondaryMuscle(customSecondaryInput);
                    }
                  }}
                  className="flex-1 bg-slate-900 border border-slate-800 text-white rounded-xl px-4 py-2.5 text-xs outline-none focus:border-indigo-500 shadow-inner font-semibold"
                />
                <button
                  type="button"
                  onClick={() => handleAddSecondaryMuscle(customSecondaryInput)}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl text-xs font-extrabold cursor-pointer transition active:scale-95"
                >
                  {formLangTab === 'pl' ? 'Dodaj Tag' : 'Add Tag'}
                </button>
              </div>

              {/* Quick Suggestions for Secondary Muscles */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(formLangTab === 'pl' ? SECONDARY_MUSCLE_SUGGESTIONS_PL : SECONDARY_MUSCLE_SUGGESTIONS_EN).map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleAddSecondaryMuscle(tag)}
                    className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-900 border border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700 transition cursor-pointer"
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CARD 3: Standard Text Execution Instructions & Hints (NOT Step-by-Step) */}
          <div className="bg-slate-950/70 p-5 rounded-2xl border border-slate-800 space-y-4 shadow-inner">
            <div className="flex items-center gap-2 text-xs font-black text-indigo-400 uppercase tracking-wider pb-2 border-b border-slate-800/80">
              <span className="material-symbols-outlined text-sm">description</span>
              <span>3. {formLangTab === 'pl' ? 'Instrukcja Wykonania i Wskazówki' : 'Execution Instructions & Hints'}</span>
            </div>

            {formLangTab === 'pl' ? (
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  Wskazówki / Instrukcja (PL)
                </label>
                <textarea
                  rows={4}
                  placeholder="Wpisz pełną instrukcję wykonania ćwiczenia oraz wskazówki dla podopiecznego (np. kontrolowany ruch, prawidłowa pozycja ciała, wydech na spięciu)..."
                  value={instructionsPL}
                  onChange={e => setInstructionsPL(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 text-slate-200 rounded-xl p-3.5 text-xs outline-none focus:border-indigo-500 shadow-inner resize-y font-medium leading-relaxed"
                />
              </div>
            ) : (
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  Instructions / Hints (EN)
                </label>
                <textarea
                  rows={4}
                  placeholder="Type execution instructions and hints for the trainee (e.g. full range of motion, controlled eccentric phase, exhale on contraction)..."
                  value={instructionsEN}
                  onChange={e => setInstructionsEN(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 text-slate-200 rounded-xl p-3.5 text-xs outline-none focus:border-indigo-500 shadow-inner resize-y font-medium leading-relaxed"
                />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Sticky Media Upload & Demonstration Panel (~35%) */}
        <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6 self-start space-y-6">
          <div className="bg-slate-950/80 p-5 rounded-2xl border border-slate-800 space-y-5 shadow-2xl">
            <div className="flex items-center gap-2 text-xs font-black text-amber-400 uppercase tracking-wider pb-2 border-b border-slate-800/80">
              <span className="material-symbols-outlined text-sm">perm_media</span>
              <span>{formLangTab === 'pl' ? 'Demonstracja i Multimedia' : 'Media & Demonstration'}</span>
            </div>

            {/* Live Media Player / Preview Frame */}
            <div className="relative w-full aspect-square bg-slate-900 border-2 border-dashed border-slate-800 rounded-2xl overflow-hidden flex flex-col items-center justify-center p-3 text-center transition group">
              {liveMediaPreviewUrl ? (
                <div className="relative w-full h-full flex flex-col items-center justify-center">
                  <img
                    src={liveMediaPreviewUrl}
                    alt="Preview"
                    className="w-full h-full object-contain rounded-xl shadow-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImageUrlInput('');
                    }}
                    className="absolute top-2 right-2 bg-rose-600 hover:bg-rose-500 text-white p-2 rounded-xl text-xs font-black cursor-pointer shadow-xl transition active:scale-95 flex items-center gap-1"
                    title={formLangTab === 'pl' ? 'Usuń plik' : 'Remove file'}
                  >
                    <span className="material-symbols-outlined text-xs">delete</span>
                    <span>{formLangTab === 'pl' ? 'Usuń' : 'Remove'}</span>
                  </button>
                </div>
              ) : (
                /* Drag & Drop File Zone */
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleFileDrop}
                  className={`w-full h-full flex flex-col items-center justify-center gap-3 p-4 rounded-xl transition cursor-pointer ${
                    isDragOver ? 'bg-indigo-600/10 border-indigo-500' : 'bg-transparent'
                  }`}
                  onClick={() => document.getElementById('exercise-file-upload-input').click()}
                >
                  <div className="w-14 h-14 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition duration-200">
                    <span className="material-symbols-outlined text-2xl">cloud_upload</span>
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-200 m-0">
                      {formLangTab === 'pl' ? 'Przeciągnij plik tutaj lub kliknij' : 'Drag & drop file here or click'}
                    </p>
                    <p className="text-[10px] text-slate-500 font-semibold m-0 mt-1">
                      PNG, JPG, GIF (Max 1 MB)
                    </p>
                  </div>
                </div>
              )}

              <input
                id="exercise-file-upload-input"
                type="file"
                accept="image/*,.gif"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file && file.size > 1 * 1024 * 1024) {
                    alert(formLangTab === 'pl'
                      ? `⚠️ Wybrany plik jest za duży (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maksymalny rozmiar to 1 MB.`
                      : `⚠️ Selected file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Max limit is 1 MB.`);
                    e.target.value = '';
                    setImageFile(null);
                    return;
                  }
                  setImageFile(file || null);
                }}
                className="hidden"
              />
            </div>

            {/* Direct Image / GIF Link Input */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <span className="material-symbols-outlined text-xs text-indigo-400">link</span>
                <span>{formLangTab === 'pl' ? 'LUB Bezpośredni Link URL Grafiki / GIFa' : 'OR Direct Picture / GIF URL'}</span>
              </label>
              <input
                type="url"
                value={imageUrlInput}
                onChange={e => setImageUrlInput(e.target.value)}
                placeholder="https://..."
                className="w-full bg-slate-900 border border-slate-800 text-slate-200 font-semibold text-xs rounded-xl px-4 py-3 outline-none focus:border-indigo-500 shadow-inner"
              />
            </div>

            {/* YouTube Video URL Input */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <span className="material-symbols-outlined text-xs text-red-500">play_circle</span>
                <span>{formLangTab === 'pl' ? 'Link URL Wideo YouTube' : 'YouTube Instruction Video URL'}</span>
              </label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full bg-slate-900 border border-slate-800 text-slate-200 font-semibold text-xs rounded-xl px-4 py-3 outline-none focus:border-indigo-500 shadow-inner"
              />
            </div>
          </div>

          {/* Sticky Bottom Action Bar */}
          <div className="bg-slate-950/90 p-4 rounded-2xl border border-slate-800 flex items-center justify-between gap-3 shadow-2xl w-full">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-5 py-3 rounded-xl text-xs font-bold transition cursor-pointer active:scale-95 shrink-0"
              >
                {formLangTab === 'pl' ? 'Anuluj' : 'Cancel'}
              </button>
            )}

            <button
              type="submit"
              disabled={isUploading}
              className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white py-3.5 px-6 rounded-xl text-xs font-black cursor-pointer shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 active:scale-95 transition"
            >
              {isUploading ? (
                <>
                  <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                  <span>{formLangTab === 'pl' ? 'Zapisywanie...' : 'Saving...'}</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-sm">save</span>
                  <span>
                    {isEdit
                      ? (formLangTab === 'pl' ? 'Zapisz Zmiany' : 'Save Changes')
                      : (formLangTab === 'pl' ? 'Dodaj Ćwiczenie' : 'Save Movement')}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default ExerciseFormBuilder;
